import { useCallback, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import {
  useAnnotationFocus,
  useSelectedVersion,
  VersionSelectorPopover,
} from '@/features/comment-on-file';
import { useUploadProjectFileVersion, VersionStaleError } from '@/features/upload-project-file';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { readBackTo } from '@/shared/lib/back-to';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBackButton,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { CopyAssetLinkButton } from '../CopyAssetLinkButton';
import { useTranslation } from 'react-i18next';

interface Props {
  folderName: string;
  folderAncestors: { id: string; name: string }[];
  fileName: string;
  projectId: string;
  folderId: string;
  fileId: string;
  fileType: string;
  latestVersionId: string | null;
  /**
   * Rightmost control: the binary approve toggle, or the chain's progress. A slot for the
   * same reason as `sidebarToggle` — on a chained asset it toggles the header's sibling.
   */
  primaryAction?: ReactNode;
  /**
   * Collapse control for the right sidebar. A slot rather than local state
   * because the sidebar it toggles is the header's sibling, not its child.
   */
  sidebarToggle?: ReactNode;
}

export function AssetDetailHeader(props: Props) {
  const { t } = useTranslation('projects');

  const { selectedVersion, versions, switchToVersion } = useSelectedVersion();
  // Version is locked while editing a comment because the update API can't
  // re-tag the comment to a new version — letting the user switch would
  // mislead them into thinking they're editing on a different version.
  const { isFocusedEditing } = useAnnotationFocus();

  const navigate = useNavigate();
  // `Location.state` is `any`, so it is narrowed here the way the rest of the app narrows it.
  const { state } = useLocation() as { state: unknown };

  // TODO(version-popover-compare-target): replace with the version the user
  // explicitly picks in the popover once the popover gains separate "active"
  // vs "compare-against" selection (sibling subtask). Until then, pair the
  // currently-viewed version with its next-most-recent neighbour as a
  // sensible default. Returns null when nothing is meaningfully compareable
  // (single version, or current version is not in the list).
  const compareTargetVersionId = useMemo(() => {
    if (!selectedVersion || versions.length < 2) return null;
    const currentIndex = versions.findIndex((v) => v.id === selectedVersion.id);
    if (currentIndex === -1) return null;
    // versions are sorted newest-first: the latest pairs with the next-newest,
    // older versions pair with the immediately-newer one above them.
    const neighbour = currentIndex === 0 ? versions[1] : versions[currentIndex - 1];
    return neighbour.id;
  }, [selectedVersion, versions]);

  const handleCompare = useCallback(() => {
    if (!selectedVersion || !compareTargetVersionId) return;
    void navigate(
      `${ROUTES.PROJECT_FILE_COMPARE(
        props.projectId,
        props.folderId,
        props.fileId,
      )}?left=${selectedVersion.id}&right=${compareTargetVersionId}`,
    );
  }, [
    selectedVersion,
    compareTargetVersionId,
    navigate,
    props.projectId,
    props.folderId,
    props.fileId,
  ]);

  const queryClient = useQueryClient();

  const uploadVersion = useUploadProjectFileVersion({
    projectId: props.projectId,
    folderId: props.folderId,
  });

  const handleUpload = useCallback(
    (file: File) => {
      uploadVersion.mutate(
        { file, fileId: props.fileId, knownLatestVersionId: props.latestVersionId },
        {
          onSuccess: (response) => {
            notificationService.showSuccess(
              t('projectDetail.assetDetailHeader.newVersionUploaded'),
            );
            switchToVersion(response.versionId);
          },
          onError: (error) => {
            if (error instanceof VersionStaleError) {
              notificationService.showWarning(
                t('projectDetail.assetDetailHeader.aNewerVersionExists'),
                {
                  description: t('projectDetail.assetDetailHeader.reloadToViewTheLatest'),
                  persist: true,
                  action: {
                    label: t('projectDetail.assetDetailHeader.reload'),
                    onClick: () => {
                      // Refetch before clearing the URL so the parent's
                      // `keepPreviousData` doesn't briefly show the stale
                      // detail / versions list against the new selection.
                      void Promise.all([
                        queryClient.refetchQueries({
                          queryKey: PROJECT_FILE_QUERY_KEYS.detail(props.projectId, props.fileId),
                        }),
                        queryClient.refetchQueries({
                          queryKey: PROJECT_FILE_QUERY_KEYS.versions(props.projectId, props.fileId),
                        }),
                      ]).then(() => {
                        // Drop ?version=… so the page falls back to the
                        // newly-refetched latest (versions[0]).
                        switchToVersion(null);
                      });
                    },
                  },
                },
              );
              return;
            }
            notificationService.showError(
              t('projectDetail.assetDetailHeader.failedToUploadNewVersion'),
              {
                anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                description: t('projectDetail.assetDetailHeader.tryAgainAndIfThe'),
              },
            );
          },
        },
      );
    },
    [
      uploadVersion,
      switchToVersion,
      queryClient,
      props.projectId,
      props.fileId,
      props.latestVersionId,
      t,
    ],
  );

  // The folder is where the asset lives, not necessarily where the person came
  // from: a tracker board opens assets across every project, and returning them
  // to one folder listing strands them in a section they never visited. A sender
  // that named a return leg wins; nobody having named one keeps the old parent.
  const backTo = readBackTo(state);
  const handleBackClick = useCallback(() => {
    void navigate(backTo ?? ROUTES.PROJECT_FOLDER_DETAIL(props.projectId, props.folderId));
  }, [navigate, backTo, props.projectId, props.folderId]);

  return (
    <PageHeader focus>
      <PageHeaderIdentity>
        <PageHeaderBackButton onClick={handleBackClick} />
        <PageHeaderBreadcrumb maxItems={4}>
          {props.folderAncestors.map((ancestor) => (
            <PageHeaderBreadcrumbLink
              key={ancestor.id}
              href={ROUTES.PROJECT_FOLDER_DETAIL(props.projectId, ancestor.id)}
            >
              {ancestor.name}
            </PageHeaderBreadcrumbLink>
          ))}
          <PageHeaderBreadcrumbLink
            href={ROUTES.PROJECT_FOLDER_DETAIL(props.projectId, props.folderId)}
          >
            {props.folderName}
          </PageHeaderBreadcrumbLink>
          <PageHeaderBreadcrumbPage>{props.fileName}</PageHeaderBreadcrumbPage>
        </PageHeaderBreadcrumb>
      </PageHeaderIdentity>
      <PageHeaderActions>
        {/* Link first, then the panel toggles — the toggles arrive as a slot,
            so this order is the header's to hold. */}
        <CopyAssetLinkButton />

        {props.sidebarToggle}

        <VersionSelectorPopover
          accept={props.fileType}
          disabled={isFocusedEditing}
          isUploading={uploadVersion.isPending}
          onUpload={handleUpload}
          onCompare={compareTargetVersionId ? handleCompare : undefined}
        />

        {props.primaryAction}
      </PageHeaderActions>
    </PageHeader>
  );
}
